/**
 * Cashflow & Age Pension — the module for a client with no care event.
 *
 * See cashflowPensionScenario.ts for why this exists and why it is deliberately general. In short:
 * CHSP has no means test, so the care cost is an INPUT the client gives us rather than an OUTPUT we
 * calculate, and the question worth answering becomes "can I afford this, and for how long?"
 *
 * Reached today through ONE door — the Support at Home overview's routing panel — by owner
 * direction. It is a page in its own right with its own router key so that opening a second door
 * later is adding a link, not a refactor. `cameFrom` lets the copy acknowledge the journey without
 * this module knowing anything about Support at Home.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { ChevronLeft, Landmark, Wallet, PiggyBank, TrendingDown, Info, AlertTriangle, ChevronDown, ShieldAlert } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId } from '../../strategy/strategyRisks.ts';
import { buildCashflowPensionSoaSnapshot } from './cpSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import { Button, GradientText, IconTile, InsightCard, CurrencyInput, ModeChooser } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import {
    runCashflowProjections,
    summarisePosition,
    type CashflowYear,
    type PositionSummary,
    type SupportCostInput,
    type BreakdownRow,
    type CashflowPensionResult,
    type ManualAllocation,
    EMPTY_ALLOCATION,
    DEFAULT_ANNUITY_TERMS,
    type ManualAnnuityTerms,
    liquidSourcesOf,
    allocationTotal,
    manualCandidateFrom,
} from './cashflowPensionScenario.ts';
import { generateCashflowPensionCandidates } from './cashflowPensionCandidates.ts';
import CashflowManualControl from './CashflowManualControl.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import { evaluateCashflowPensionCandidates } from '../../services/calculationApi.ts';
import { bestForEachGoal } from '../../../../shared/src/calculation/cashflowPensionRanking.ts';

type WorkingKey = 'pension' | 'cashflow' | 'networth' | 'estate';

const WORKING_TITLES: Record<WorkingKey, string> = {
    pension: 'How your Age Pension is worked out',
    cashflow: 'Where your cashflow comes from',
    networth: 'What makes up your net worth',
    estate: 'How the estate figure is reached',
};

export interface CashflowPensionPageProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
    /** Where the client came from, so the copy can acknowledge it. 'support_at_home' today. */
    cameFrom?: string;
    /**
     * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
     * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
}

/**
 * One headline figure. The card carries the NUMBER only.
 *
 * Its working opens full-width BELOW the row rather than inside the card, which was the first cut
 * and was unreadable: four cards across a 1330px viewport leaves each about 180px, and an asset-test
 * working set that narrow wrapped to one word a line ("Your / Assets", "Full / Pension / Limit").
 * The same lesson the narrative JargonTerm already records — prose does not belong in a 150px
 * column. Caught on screen, not in a test.
 */
const PositionCard = ({
    icon,
    label,
    value,
    suffix,
    hint,
    tone = 'brand',
    open,
    onToggle,
}: {
    icon: typeof Wallet;
    /** Node, not string: the estate card carries an inline years control (ProjectionYearsInput). */
    label: React.ReactNode;
    value: string;
    suffix?: string;
    hint: string;
    tone?: 'brand' | 'emerald' | 'amber' | 'slate';
    open?: boolean;
    onToggle?: () => void;
}) => (
    <div className={`rounded-2xl border bg-white p-5 shadow-card transition-colors ${open ? 'border-brand-blue' : 'border-slate-200/70'}`}>
        <div className="flex items-start gap-3">
            <IconTile icon={icon} tone={tone} variant="solid" size="md" />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-600">{label}</p>
                <p className="mt-0.5 text-[26px] font-bold leading-tight tracking-tight text-gray-900">
                    {value}
                </p>
                {suffix && <p className="text-xs text-slate-500">{suffix}</p>}
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{hint}</p>
                {onToggle && (
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={!!open}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                    >
                        {open ? 'Hide working' : 'How we got here'}
                        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                )}
            </div>
        </div>
    </div>
);

/**
 * The engine's own working, rendered as it comes.
 *
 * These arrive as an ORDERED ARRAY read top to bottom, not a keyed object — my first cut looked up
 * keys like `assessableAssets`, every lookup missed, and the card would have shown a tidy column of
 * zeros with all the authority of a real calculation. Exactly the defect shape this codebase keeps
 * meeting, and it would have been invisible on screen.
 *
 * Three row shapes have to be honoured: `--- Asset Test ---` separators become section headings, a
 * row with an empty value is a note rather than a figure, and `value` is sometimes already a
 * formatted string ("Part Pension", "$3 per fortnight per $1000") and sometimes a raw number.
 */
const BreakdownList = ({ rows }: { rows?: BreakdownRow[] }) => {
    if (!rows?.length) {
        return <p className="text-slate-500">No working available for this figure.</p>;
    }
    return (
        <div className="space-y-0.5">
            {rows.map((row, i) => {
                const heading = /^-{2,}\s*(.+?)\s*-{2,}$/.exec(String(row.label));
                if (heading) {
                    return (
                        <p key={i} className="mt-2 border-t border-slate-200 pt-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 first:mt-0 first:border-0 first:pt-0">
                            {heading[1]}
                        </p>
                    );
                }
                if (row.value === '' || row.value == null) {
                    return <p key={i} className="text-slate-500">{row.label}</p>;
                }
                return (
                    <div
                        key={i}
                        className={`flex items-baseline justify-between gap-4 ${row.emphasis ? 'font-semibold text-slate-800' : ''}`}
                    >
                        <span>{row.label}</span>
                        <span className="shrink-0 tabular-nums">
                            {typeof row.value === 'number' ? formatCurrency(row.value) : row.value}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const GOAL_LABELS = {
    cashflow: 'Best cashflow',
    pension: 'Best Age Pension',
    estate: 'Best estate',
} as const;

/**
 * A change against doing nothing.
 *
 * The tone rule is the app's shared one (components/strategy/recapClauses.tsx): green where the
 * movement is GOOD FOR THE CLIENT, amber where it is bad, plain where nothing moved. Not amber for
 * "no change" — amber means something needs attention, and nothing moving needs none.
 *
 * These are chips rather than a sentence because this module shows three options side by side, and
 * a column of aligned figures compares far better than three paragraphs. Same vocabulary, different
 * shape for a different job.
 */
const Delta = ({ label, amount, good }: { label: string; amount: number; good: 'up' | 'down' }) => {
    const rounded = Math.round(amount);
    const positive = rounded > 0;
    const neutral = rounded === 0;
    const helpful = good === 'up' ? positive : !positive;
    return (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <p className={`text-sm font-bold tabular-nums ${neutral ? 'text-slate-500' : helpful ? 'text-emerald-700' : 'text-amber-700'}`}>
                {neutral ? 'No change' : `${positive ? '+' : '−'}${formatCurrency(Math.abs(rounded))}`}
            </p>
        </div>
    );
};

const CashflowPensionPage: React.FC<CashflowPensionPageProps> = ({ setActivePage, factFind, setSavedStrategies, cameFrom, onSoaGenerated }) => {
    const [years, setYears] = useState<CashflowYear[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [legacyYears, setLegacyYears] = useState(10);

    /*
     * The support cost is held HERE, not on the fact-find (owner direction). It is the client's
     * figure, taken from their provider's bill, and keeping it in module state means no schema
     * change and no risk of it being mistaken for something we calculated.
     */
    const [supportAnnual, setSupportAnnual] = useState<number | ''>('');
    const [supportLabel, setSupportLabel] = useState('Support at home services');

    const supportCost: SupportCostInput | null = useMemo(
        () => (typeof supportAnnual === 'number' && supportAnnual > 0 ? { annual: supportAnnual, label: supportLabel } : null),
        [supportAnnual, supportLabel],
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        runCashflowProjections(factFind, { legacyYears, supportCost })
            .then((result) => {
                if (cancelled) return;
                setYears(result);
                setLoading(false);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : 'Could not work out your position.');
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [factFind, legacyYears, supportCost]);

    const position: PositionSummary | null = useMemo(() => (years ? summarisePosition(years) : null), [years]);
    const first = years?.[0];

    /* One working open at a time — four expanded panels at once is a wall, not an explanation. */
    const [openWorking, setOpenWorking] = useState<WorkingKey | null>(null);
    const toggle = (key: WorkingKey) => setOpenWorking((current) => (current === key ? null : key));

    /*
     * Strategies are loaded ON REQUEST, not on mount. Each candidate is a full projection over the
     * horizon, so generating them unasked would make the page slow to open for every client — most
     * of whom came to see where they stand, not to be sold a strategy.
     */
    const [strategies, setStrategies] = useState<CashflowPensionResult[] | null>(null);
    const [strategiesLoading, setStrategiesLoading] = useState(false);
    const [strategiesError, setStrategiesError] = useState<string | null>(null);

    /*
     * MODE PARITY WITH EVERY OTHER MODULE, and '' until they choose — the mode choice is the first
     * act, the same as Aged Care and the Downsizer, so a new client does not land with Manual
     * Control's levers already spread out in front of them.
     */
    const [strategyMode, setStrategyMode] = useState<'' | 'manual' | 'optimised'>('');
    const [allocation, setAllocation] = useState<ManualAllocation>(EMPTY_ALLOCATION);
    /*
     * The annuity's TERMS, held beside the amount rather than inside it: `allocationTotal` sums the
     * allocation map, so a non-numeric field there would corrupt the total. Owner-directed
     * (2026-09-01) — this module used to decide all five of these itself.
     */
    const [annuityTerms, setAnnuityTerms] = useState<ManualAnnuityTerms>(DEFAULT_ANNUITY_TERMS);
    /*
     * A real provider quote for the SUGGESTION ENGINE, and the draft being typed, kept apart — only
     * the committed value re-runs the field, so typing a digit does not fire a run per keystroke.
     */
    const [seAnnuityQuote, setSeAnnuityQuote] = useState<number | null>(null);
    const [seAnnuityQuoteDraft, setSeAnnuityQuoteDraft] = useState<number | string>('');
    const [manualResult, setManualResult] = useState<CashflowPensionResult[] | null>(null);
    const [manualLoading, setManualLoading] = useState(false);
    /*
     * The plans themselves, kept so a save records exactly what was run: the Action Plan rebuilds the
     * position after the plan from them, to fill the forms (owner, 3 Oct 2026).
     */
    const [manualCandidate, setManualCandidate] = useState<unknown>(null);
    const [engineCandidates, setEngineCandidates] = useState<Array<{ id: string }>>([]);

    const [strategyName, setStrategyName] = useState('');
    const [saveNote, setSaveNote] = useState('');
    /** Which engine option they picked, so a saved plan records WHAT was chosen, not just the mode. */
    const [selectedEngineId, setSelectedEngineId] = useState<string | null>(null);
    // "What could go wrong" for one option (StrategyRisksPanel).
    const [riskOption, setRiskOption] = useState<CashflowPensionResult | null>(null);

    /** The Statement of Advice for the chosen option (cpSoa.ts) — opened, downloaded, registered. */
    const generateCpSoa = (option: CashflowPensionResult) => {
        const leaders = Object.entries(best || {})
            .filter(([, winner]) => winner)
            .map(([goal, winner]) => ({ label: GOAL_LABELS[goal as keyof typeof GOAL_LABELS], id: (winner as CashflowPensionResult).id }));
        const snapshot = buildCashflowPensionSoaSnapshot({ option, all: strategies || [], leaders, factFind, legacyYears, supportCost });
        openModuleSoa(snapshot);
        onSoaGenerated?.({ id: option.id, name: option.name, type: 'cashflow_pension', source: 'suggestion_engine', strategy: option, soaSnapshot: snapshot });
    };

    const sources = useMemo(() => liquidSourcesOf(factFind), [factFind]);
    const totalDebt = useMemo(() => {
        const liabilities: any = factFind?.liabilities || {};
        return Object.values(liabilities)
            .flatMap((rows: any) => (Array.isArray(rows) ? rows : []))
            .reduce((sum: number, row: any) => sum + (parseFloat(String(row?.balance ?? '')) || 0), 0);
    }, [factFind]);

    /*
     * Re-rank when the quote is committed. Only when a field is already showing: before the client
     * has asked for options, there is nothing to re-rank and a run would be work they did not ask
     * for.
     */
    useEffect(() => {
        if (strategyMode !== 'optimised') return;
        if (!strategies && !strategiesLoading) return;
        loadStrategies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seAnnuityQuote]);

    const loadStrategies = async () => {
        setStrategiesLoading(true);
        setStrategiesError(null);
        try {
            const candidates = generateCashflowPensionCandidates(factFind, seAnnuityQuote);
            setEngineCandidates(candidates as Array<{ id: string }>);
            const results = await evaluateCashflowPensionCandidates(
                factFind,
                candidates,
                legacyYears,
                supportCost?.annual || 0,
            ) as CashflowPensionResult[];
            setStrategies(results || []);
        } catch (e) {
            setStrategiesError(e instanceof Error ? e.message : 'We couldn’t model your options just now.');
        } finally {
            setStrategiesLoading(false);
        }
    };

    /*
     * A manual allocation is turned into the SAME candidate shape the engine ranks, and evaluated by
     * the same call. Two code paths to one answer is how a module comes to show a client two
     * different numbers for the same plan.
     */
    useEffect(() => {
        if (strategyMode !== 'manual') return undefined;
        const allocated = allocationTotal(allocation);
        if (allocated <= 0) { setManualResult(null); return undefined; }

        let cancelled = false;
        setManualLoading(true);
        const timer = setTimeout(() => {
            const candidate = manualCandidateFrom(allocation, annuityTerms, sources);
            setManualCandidate(candidate);
            evaluateCashflowPensionCandidates(
                factFind,
                [{ id: 'cp-nothing', name: 'Change nothing', description: '', changeNothing: true }, candidate],
                legacyYears,
                supportCost?.annual || 0,
            )
                .then((r: unknown) => { if (!cancelled) setManualResult((r || []) as CashflowPensionResult[]); })
                .catch(() => { if (!cancelled) setManualResult(null); })
                .finally(() => { if (!cancelled) setManualLoading(false); });
        }, 500); // typing settles before a full projection is run

        return () => { cancelled = true; clearTimeout(timer); };
    }, [strategyMode, allocation, annuityTerms, sources, factFind, legacyYears, supportCost]);

    /* Any change to the inputs invalidates a ranking built on the old ones. */
    useEffect(() => { setStrategies(null); }, [factFind, legacyYears, supportCost]);

    const best = useMemo(() => (strategies ? bestForEachGoal(strategies) : null), [strategies]);

    return (
        <div className="mx-auto max-w-6xl">
            <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setActivePage('HomeCarePage')} className="mb-4 -ml-2">
                Back
            </Button>

            <div className="mb-2 flex items-center gap-4">
                <IconTile icon={Wallet} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
                <h1 className="text-4xl font-bold text-gray-900">
                    <GradientText>Your Cashflow &amp; Age Pension</GradientText>
                </h1>
            </div>
            <p className="mb-8 text-lg leading-relaxed text-slate-700">
                {cameFrom === 'support_at_home'
                    ? 'None of this needs a care assessment. Here’s where you stand today, how each figure is worked out, and what happens if you start paying for support at home.'
                    : 'Where you stand today, how each figure is worked out, and what happens as your costs change.'}
            </p>

            {error && (
                <InsightCard tone="caution" heading="We couldn’t work out your position" icon={AlertTriangle} className="mb-6">
                    <p>{error}</p>
                    <p className="mt-2 text-slate-500">
                        This usually means something in your profile is incomplete. Check your assets and income, then come back.
                    </p>
                </InsightCard>
            )}

            {loading && !years && (
                <div className="rounded-2xl border border-slate-200/70 bg-white p-10 text-center shadow-card">
                    <p className="text-sm text-slate-500">Working out your position…</p>
                </div>
            )}

            {position && (
                <>
                    <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <PositionCard
                            icon={Landmark}
                            label="Age Pension"
                            value={formatCurrency(position.agePension)}
                            suffix="a year"
                            hint="What Centrelink would pay on your current assets and income."
                            open={openWorking === 'pension'}
                            onToggle={() => toggle('pension')}
                        />
                        <PositionCard
                            icon={Wallet}
                            label="Cashflow"
                            value={formatCurrency(position.cashFlow)}
                            suffix="a year"
                            hint="Money in, less money out, over the next 12 months."
                            tone={position.cashFlow < 0 ? 'amber' : 'emerald'}
                            open={openWorking === 'cashflow'}
                            onToggle={() => toggle('cashflow')}
                        />
                        <PositionCard
                            icon={PiggyBank}
                            label="Net worth today"
                            value={formatCurrency(position.netWorth)}
                            hint="Everything you own, less everything you owe."
                            tone="slate"
                            open={openWorking === 'networth'}
                            onToggle={() => toggle('networth')}
                        />
                        <PositionCard
                            icon={TrendingDown}
                            label={<ProjectionYearsInput years={legacyYears} onChange={setLegacyYears} before="Estate in" />}
                            value={formatCurrency(position.estate)}
                            hint="What would be left if nothing changes."
                            tone="slate"
                            open={openWorking === 'estate'}
                            onToggle={() => toggle('estate')}
                        />
                    </div>

                    {/* Which test sets the pension, and what would have to move — the same two beats
                        every module carries (owner, 24 Sep 2026). Read from the first year's own
                        breakdown, so it is the frame the cards above were struck in. */}
                    <PlainWordsCard eyebrow={`Where ${factFind?.client?.firstName || 'you'} ${factFind?.client?.firstName ? 'stands' : 'stand'} today`} className="mb-4" collapsible>
                        <MeansTestBeats careDepositRelevant={Boolean(factFind?.goals?.aged_care)} financials={first as { socialSecurity?: number; socialSecurityBreakdown?: unknown } | undefined} isCouple={factFind?.adviceFor === 'couple'} name={factFind?.client?.firstName || 'you'} whereNext="The options below" />
                    </PlainWordsCard>

                    {/*
                      * The working, full width. Two columns on a wide screen so a long asset-test
                      * walk does not become a single tall ribbon, and each row keeps enough width
                      * for its label to sit on one line.
                      */}
                    {openWorking && (
                        <div className="mb-6 rounded-2xl border border-brand-blue/40 bg-white p-5 shadow-card">
                            <h3 className="mb-3 text-base font-bold text-gray-800">{WORKING_TITLES[openWorking]}</h3>
                            <div className="text-[13px] leading-relaxed text-slate-600 md:columns-2 md:gap-8">
                                {openWorking === 'pension' && <BreakdownList rows={first?.socialSecurityBreakdown} />}
                                {openWorking === 'cashflow' && <BreakdownList rows={first?.cashFlowBreakdown} />}
                                {openWorking === 'networth' && <BreakdownList rows={first?.netWorthBreakdown} />}
                                {openWorking === 'estate' && (
                                    <BreakdownList
                                        rows={[
                                            { label: 'Net worth today', value: position.netWorth },
                                            { label: 'Change over the period', value: position.estate - position.netWorth },
                                            { label: `Estate in ${legacyYears} years`, value: position.estate, emphasis: true },
                                        ]}
                                    />
                                )}
                            </div>
                            {openWorking === 'pension' && (
                                <p className="mt-3 border-t border-slate-200 pt-3 text-[13px] text-slate-500">
                                    Centrelink works out both tests and pays whichever gives the <strong>lower</strong> amount.
                                </p>
                            )}
                            {openWorking === 'cashflow' && position.cashFlow < 0 && (
                                <p className="mt-3 border-t border-slate-200 pt-3 text-[13px] text-amber-700">
                                    You&apos;re spending more than you receive, so savings are being drawn down. That is not
                                    automatically wrong — it is what savings are for — but it is worth seeing.
                                </p>
                            )}
                            {openWorking === 'estate' && (
                                <p className="mt-3 border-t border-slate-200 pt-3 text-[13px] text-slate-500">
                                    In today&apos;s dollars, after living costs, investment returns and any support you pay for.
                                </p>
                            )}
                        </div>
                    )}

                    {position.shortfallYear != null && (
                        <InsightCard tone="caution" heading="Your savings run short" icon={AlertTriangle} className="mb-6">
                            <p>
                                On these figures the plan can no longer be funded from about{' '}
                                <strong>year {position.shortfallYear}</strong>. That is the point worth planning around —
                                not a prediction, but a signal that the current course needs a change somewhere.
                            </p>
                        </InsightCard>
                    )}

                    <div className="mb-6 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card">
                        <div className="flex items-start gap-3">
                            <IconTile icon={Info} tone="brand" variant="soft" size="md" />
                            <div className="flex-1">
                                <h3 className="text-base font-bold text-gray-800">What are you paying for support at home?</h3>
                                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                                    If you already get help at home — cleaning, meals, transport, personal care — put the
                                    yearly cost in here and everything above updates. <strong>We can&apos;t work this out
                                    for you</strong>: entry-level support fees are set by each provider, so the figure has
                                    to come from your provider or your invoices.
                                </p>
                                <div className="mt-3 flex flex-wrap items-end gap-3">
                                    <div className="w-56">
                                        <CurrencyInput
                                            label="Yearly cost of support"
                                            value={supportAnnual}
                                            onChange={(v) => setSupportAnnual(v ?? '')}
                                            className=""
                                        />
                                    </div>
                                    <div className="w-64">
                                        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="cp-support-label">
                                            What is it for?
                                        </label>
                                        <input
                                            id="cp-support-label"
                                            type="text"
                                            value={supportLabel}
                                            onChange={(e) => setSupportLabel(e.target.value)}
                                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue"
                                        />
                                    </div>
                                    <div className="w-40">
                                        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="cp-years">
                                            Project over
                                        </label>
                                        <select
                                            id="cp-years"
                                            value={legacyYears}
                                            onChange={(e) => setLegacyYears(parseInt(e.target.value, 10))}
                                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue"
                                        >
                                            {[5, 10, 15, 20].map((y) => (
                                                <option key={y} value={y}>{y} years</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {supportCost && (
                                    <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">
                                        <strong>You told us</strong> {supportLabel.toLowerCase()} costs{' '}
                                        {formatCurrency(supportCost.annual)} a year. The figures above include it.{' '}
                                        Paying for support does <strong>not</strong> change your Age Pension — that is
                                        assessed on what you own and earn, not on what you spend.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/*
                      * STEP 3 — what could improve this. Same levers every other module already
                      * shares; the only thing different here is the OBJECTIVE, because there is no
                      * care contribution to reduce. Housing moves are deliberately absent: they have
                      * their own modules, and a fifth implementation of a home sale would drift.
                      */}
                    <div className="mb-6 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card">
                        <h3 className="mb-1 text-base font-bold text-gray-800">What could improve your position</h3>
                        <p className="text-sm leading-relaxed text-gray-600">
                            Ways to improve your Age Pension, your cashflow or what you leave behind — without
                            moving house. Each is measured against <strong>changing nothing</strong>, and each shows
                            what it costs you as well as what it gains.
                        </p>

                        <div className="mt-4">
                            <ModeChooser
                                heading={null}
                                value={strategyMode}
                                onSelect={(id) => setStrategyMode(id as 'manual' | 'optimised')}
                                options={[
                                    {
                                        id: 'manual',
                                        title: 'Manual Control',
                                        blurb: 'Move your own money — into a funeral bond, a gift, an annuity or off your debt — and watch the three figures change.',
                                        chip: 'Advisers & confident users',
                                    },
                                    {
                                        id: 'optimised',
                                        title: 'Suggestion Engine',
                                        blurb: 'It models a few ways to rearrange what you have, then shows the strongest for each goal.',
                                        chip: 'Most people start here',
                                    },
                                ]}
                            />
                        </div>

                        {strategyMode === 'manual' && (
                            <div className="mt-4 space-y-4">
                                <CashflowManualControl
                                    factFind={factFind}
                                    sources={sources}
                                    allocation={allocation}
                                    annuityTerms={annuityTerms}
                                    onAnnuityTermsChange={setAnnuityTerms}
                                    onChange={setAllocation}
                                    totalDebt={totalDebt}
                                />
                                {manualLoading && <p className="text-sm text-slate-500">Working out what that does…</p>}
                                {(() => {
                                    const mine = manualResult?.find((r) => r.id === 'cp-manual');
                                    if (!mine || !mine.versusBaseline) return null;
                                    return (
                                        <div className="rounded-xl border border-brand-blue/40 bg-white p-4">
                                            <p className="mb-2 text-sm font-bold text-gray-800">What your plan does</p>
                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                                <Delta label="Age Pension" amount={mine.versusBaseline.pension} good="up" />
                                                <Delta label="Cashflow" amount={mine.versusBaseline.cashflow} good="up" />
                                                <Delta label={`Estate in ${legacyYears} yrs`} amount={mine.versusBaseline.estate} good="up" />
                                            </div>
                                            <p className="mt-2 text-xs text-slate-500">
                                                Measured against changing nothing, on the same projection the Suggestion Engine uses.
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {strategyMode === 'optimised' && !strategies && !strategiesLoading && (
                            <div className="mt-4">
                                <Button variant="brand" size="sm" onClick={loadStrategies}>See my options</Button>
                            </div>
                        )}

                        {strategiesLoading && <p className="mt-4 text-sm text-slate-500">Modelling your options…</p>}
                        {strategiesError && (
                            <p className="mt-4 text-sm text-amber-700">{strategiesError}</p>
                        )}

                        {strategyMode === 'optimised' && strategies && strategies.length > 0 && (
                            <div className="mt-4 space-y-3">
                                {strategies.filter((s) => !s.changeNothing).map((option) => {
                                    const delta = option.versusBaseline;
                                    const badges = Object.entries(best || {})
                                        .filter(([, winner]) => winner && winner.id === option.id)
                                        .map(([goal]) => GOAL_LABELS[goal as keyof typeof GOAL_LABELS]);
                                    const chosen = selectedEngineId === option.id;
                                    return (
                                        /*
                                         * The terms sit OUTSIDE the card button: the explainer is itself a
                                         * button, and a button cannot be nested inside another one — the
                                         * markup is invalid and the click lands on whichever the browser
                                         * decides. Shown once the option is chosen, which is the point the
                                         * client is deciding whether to commit the money.
                                         */
                                        <div key={option.id}>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedEngineId(chosen ? null : option.id)}
                                            aria-pressed={chosen}
                                            className={`block w-full rounded-xl border p-4 text-left transition-colors ${chosen ? 'border-brand-blue bg-blue-50/40' : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'}`}
                                        >
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h4 className="text-sm font-bold text-gray-800">{option.name}</h4>
                                                        {badges.map((b) => (
                                                            <span key={b} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-800">
                                                                {b}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <p className="mt-1 text-xs leading-relaxed text-slate-600">{option.description}</p>
                                                </div>
                                            </div>
                                            {option.error ? (
                                                <p className="mt-3 text-xs text-amber-700">
                                                    We couldn&apos;t model this one on your figures.
                                                </p>
                                            ) : delta ? (
                                                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                                    <Delta label="Age Pension" amount={delta.pension} good="up" />
                                                    <Delta label="Cashflow" amount={delta.cashflow} good="up" />
                                                    {/*
                                                      * ALWAYS SHOWN, never optional. Most of these levers improve the
                                                      * pension by spending capital, so the estate is where the cost
                                                      * lands. A card showing only the gain would be selling, not advising.
                                                      */}
                                                    <Delta label={`Estate in ${legacyYears} yrs`} amount={delta.estate} good="up" />
                                                </div>
                                            ) : null}
                                            {chosen && (
                                                <p className="mt-2 text-xs font-semibold text-blue-700">
                                                    Selected — name it below to save this plan.
                                                </p>
                                            )}
                                        </button>
                                        {/* Outside the card button (a button cannot hold a button): the honest
                                            list of this option's risks, offered once it is chosen. */}
                                        {chosen && (
                                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2 text-sm text-emerald-900 shadow-sm">
                                                <p className="min-w-0 flex-1"><span className="font-semibold">This is your selected plan.</span> Name it below to save it with your file.</p>
                                                <button
                                                    type="button"
                                                    onClick={() => generateCpSoa(option)}
                                                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                                                >
                                                    Generate your Statement of Advice →
                                                </button>
                                            </div>
                                        )}
                                        {chosen && (
                                            <button
                                                type="button"
                                                onClick={() => setRiskOption(option)}
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                                            >
                                                <ShieldAlert size={14} aria-hidden /> What could go wrong
                                            </button>
                                        )}
                                        {chosen && (
                                            <EngineAnnuityTerms
                                                className="mt-2"
                                                terms={option.annuityTerms}
                                                factFind={factFind}
                                                /*
                                                 * A REAL QUOTE, and the whole field re-ranked on it.
                                                 * The rate is what the decision turns on, and until
                                                 * now this module could only rank on ours (owner,
                                                 * 2026-09-01). Committing one re-runs every option,
                                                 * because a different rate can change which wins —
                                                 * relabelling this card would leave the ranking
                                                 * built on a number nobody is being offered.
                                                 */
                                                quote={<AnnuityQuoteControl
                                                    draft={seAnnuityQuoteDraft}
                                                    onDraft={setSeAnnuityQuoteDraft}
                                                    committed={seAnnuityQuote}
                                                    onCommit={setSeAnnuityQuote}
                                                />}
                                            />
                                        )}
                                        </div>
                                    );
                                })}
                                <p className="pt-1 text-xs leading-relaxed text-slate-500">
                                    Figures are a change against doing nothing, on your own numbers. Moving house — downsizing,
                                    a retirement village or a granny flat — is modelled in its own section, because the trade-offs
                                    there are different.
                                </p>
                            </div>
                        )}

                        {strategyMode === 'optimised' && strategies && strategies.filter((s) => !s.changeNothing).length === 0 && !strategiesLoading && (
                            <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                On your current figures there&apos;s nothing here we&apos;d suggest changing. That is a real
                                answer, not a gap — these options need spare capital beyond an emergency reserve to be worth doing.
                            </p>
                        )}
                    </div>

                    {/*
                      * SAVE AND COMPARE, like every other module. The saved record carries what was
                      * chosen — the manual allocation, or the engine option selected — so reopening it
                      * shows the same plan rather than a blank page in the right mode.
                      *
                      * `type: 'cashflow_pension'` is its OWN type, not filed under home care. A
                      * strategy is filed by what it IS, not by which door the client came through:
                      * these are cashflow and pension strategies, and the entry point is expected to
                      * change once the module is opened from elsewhere.
                      */}
                    {setSavedStrategies && strategyMode && (
                        <div className="mb-6 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card">
                            <h3 className="text-base font-bold text-gray-800">Save this plan</h3>
                            <p className="mt-0.5 text-sm text-gray-600">
                                Keep it for the dashboard and to compare against other plans later.
                            </p>
                            <div className="mt-3 flex flex-wrap items-end gap-3">
                                <div className="min-w-[16rem] flex-1">
                                    <label htmlFor="cp-name" className="mb-1 block text-sm font-medium text-gray-700">
                                        Give it a name
                                    </label>
                                    <input
                                        id="cp-name"
                                        type="text"
                                        value={strategyName}
                                        placeholder={'e.g. "Funeral bonds and a small gift"'}
                                        onChange={(e) => setStrategyName(e.target.value)}
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue"
                                    />
                                </div>
                                <Button
                                    variant="brand"
                                    onClick={() => {
                                        const trimmed = strategyName.trim();
                                        if (!trimmed) { setSaveNote('Please give the plan a name first.'); return; }
                                        const chosen = strategyMode === 'manual'
                                            ? manualResult?.find((r) => r.id === 'cp-manual')
                                            : strategies?.find((r) => r.id === selectedEngineId);
                                        if (!chosen) {
                                            setSaveNote(strategyMode === 'manual'
                                                ? 'Allocate something above before saving.'
                                                : 'Choose one of the options above before saving.');
                                            return;
                                        }
                                        setSavedStrategies((prev) => [
                                            ...(prev || []),
                                            {
                                                id: `cp-${Date.now()}`,
                                                name: trimmed,
                                                type: 'cashflow_pension',
                                                inputs: {
                                                    source: strategyMode === 'manual' ? 'manual' : 'suggestion_engine',
                                                    allocation: strategyMode === 'manual' ? { ...allocation } : null,
                                                    engineOptionId: strategyMode === 'manual' ? null : chosen.id,
                                                    engineOptionName: strategyMode === 'manual' ? null : chosen.name,
                                                    // The plan as run, so its position can be rebuilt for the forms.
                                                    candidate: strategyMode === 'manual' ? manualCandidate : (engineCandidates.find((c) => c.id === chosen.id) ?? null),
                                                    supportCostAnnual: supportCost?.annual ?? 0,
                                                    supportCostLabel: supportCost?.label ?? null,
                                                    legacyYears,
                                                },
                                                outputs: {
                                                    agePension: chosen.snapshot.pension,
                                                    cashFlow: chosen.snapshot.cashflow,
                                                    legacyEstimate: chosen.snapshot.estate,
                                                },
                                                createdAt: new Date().toISOString(),
                                            },
                                        ]);
                                        setStrategyName('');
                                        setSaveNote('Saved — it now appears in the dashboard’s Cashflow & Age Pension section.');
                                    }}
                                >
                                    Save plan
                                </Button>
                            </div>
                            {saveNote && <p className="mt-2 text-sm text-slate-600">{saveNote}</p>}
                        </div>
                    )}
                </>
            )}
        {riskOption && (
                <StrategyRisksPanel
                    strategyName={riskOption.name}
                    onClose={() => setRiskOption(null)}
                    risks={buildStrategyRisks(riskKindsFromCandidateId(String(riskOption.id || '')))}
                />
            )}
            </div>
    );
};

export default CashflowPensionPage;
